import React from 'react';
import { Outlet } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

import { usePointerGlow } from '../../hooks/usePointerGlow';
import { CustomizerProvider, useCustomizer } from './store/customizer';
import { WizardHeader } from './components/WizardHeader';
import { ProgressPanel } from './components/ProgressPanel';
import { WizardFooter } from './components/WizardFooter';
import { DesktopStepSlide, resetDesktopStepSlide } from './components/DesktopStepSlide';
import { LogoProcessingOverlay } from './components/LogoProcessingOverlay';
import { MobileApp } from './mobile/MobileApp';
import {
  DesktopShell,
  Main,
  LeftCol,
  CenterCol,
  RightCol,
  FooterRow,
  ErrorScreen,
  ErrorTitle,
  ErrorBody,
} from './customizer.styles';

/**
 * Renders the active step inside the desktop wizard shell (header · [step body ·
 * progress panel] · footer), or the dedicated mobile experience. Must live inside
 * `CustomizerProvider` (reads its status / breakpoint).
 */
/**
 * Whole-screen loading state, mounted above the wizard (desktop + mobile) while
 * an owner-uploaded logo is uploaded and background-removed. Lives outside
 * `CustomizerInner` so it overlays whichever shell is rendered.
 */
const LogoProcessingGate = (): React.ReactElement | null => {
  const { logoProcessing } = useCustomizer();
  return logoProcessing ? <LogoProcessingOverlay /> : null;
};

const CustomizerInner = (): React.ReactElement => {
  const { isMobile, status, stepKey, step } = useCustomizer();

  // Drive the shared cursor-following hover glow for this wizard's CTAs (the deploy
  // flow renders outside MainLayout, which is where the controller usually lives).
  usePointerGlow();

  // The desktop step-slide remembers the previous step in module scope (it remounts
  // per step via `key`, so it can't hold that in component state). That memory
  // survives an SPA exit/re-entry of the wizard, which would otherwise slide a new
  // visit's first step in from the wrong side. Re-baseline it once, on this root's
  // first render — before any DesktopStepSlide child reads it — so each fresh entry
  // fades in neutrally (matching a cold page load).
  const reentry = React.useRef(false);
  if (!reentry.current) {
    reentry.current = true;
    resetDesktopStepSlide();
  }

  if (isMobile) return <MobileApp />;

  if (status === 'error') {
    return (
      <ErrorScreen>
        <ErrorTitle text="Something went wrong" tag="h1" />
        <ErrorBody text="We couldn't load the setup. Please refresh and try again." tag="paragraph" />
      </ErrorScreen>
    );
  }

  // Step 4 (deploy / "Go live") owns its whole layout — its own header, the
  // device-mockup preview, and the deploy sidebar — so the shared wizard
  // header / progress panel / footer don't render.
  if (stepKey === 'deploy') {
    return (
      <DesktopShell>
        {/* Step 4 owns its whole layout, so the slide carries the entire deploy
            body (no shared sidebar to keep fixed here). */}
        <DesktopStepSlide key={step} step={step}>
          <Outlet />
        </DesktopStepSlide>
      </DesktopShell>
    );
  }

  return (
    <DesktopShell>
      <WizardHeader />
      <Main>
        <LeftCol>
          {/* Only the step body slides between steps (keyed by step so each change
              replays the entrance); the footer below and the progress sidebar to the
              right stay fixed — mirroring the mobile chrome. */}
          <CenterCol>
            <DesktopStepSlide key={step} step={step}>
              <Outlet />
            </DesktopStepSlide>
          </CenterCol>
          <FooterRow>
            <WizardFooter />
          </FooterRow>
        </LeftCol>
        <RightCol>
          <ProgressPanel />
        </RightCol>
      </Main>
    </DesktopShell>
  );
};

/**
 * customizer area root — the wizard's layout route. Wraps every step in the
 * shared `CustomizerProvider` (so wizard state persists across step navigation)
 * and sets the browser-tab title. Chrome (side rail + auth gate) comes from
 * `CustomizerLayout`; the wizard owns its own header/footer/progress panel.
 */
export const CustomizerRoot = (): React.ReactElement => {
  const { t } = useTranslation();
  return (
    <>
      <Helmet>
        <title>{`${t('Take your website live')} - Whitelabels.com`}</title>
      </Helmet>
      <CustomizerProvider>
        <CustomizerInner />
        <LogoProcessingGate />
      </CustomizerProvider>
    </>
  );
};

export default CustomizerRoot;